import React, { useMemo, useState } from "react";
import { RotateCcw, AlertCircle } from "lucide-react";
import CurrencyBuffer from "./CurrencyBuffer";

import { tr } from "../i18n";
const TERMS = [3, 6, 9, 12, 18, 24, 36, 60];

const eur = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const fmtEur = (n) => eur.format(n);

// Parse a number accepting both English (1,234.56) and German (1.234,56) input.
function parseNum(str) {
  if (str === null || str === undefined) return NaN;
  let s = String(str).trim().replace(/[€\s]/g, "");
  if (!s) return NaN;
  if (s.includes(",") && s.includes(".")) {
    if (s.lastIndexOf(",") > s.lastIndexOf(".")) {
      s = s.replace(/\./g, "").replace(",", "."); // German
    } else {
      s = s.replace(/,/g, ""); // English
    }
  } else if (s.includes(",")) {
    // treat a single comma as thousands if 3 digits follow, else as decimal
    const parts = s.split(",");
    s = parts.length === 2 && parts[1].length === 3 ? s.replace(",", "") : s.replace(",", ".");
  }
  return parseFloat(s);
}

export default function InterestCalculator() {
  const [amount, setAmount] = useState("50,000");
  const [rate, setRate] = useState("4.00");
  const [term, setTerm] = useState(12);
  const [touched, setTouched] = useState({ amount: false, rate: false });

  const amountVal = parseNum(amount);
  const rateVal = parseNum(rate);

  const amountError = !touched.amount
    ? ""
    : isNaN(amountVal)
    ? tr("Please enter a valid amount.")
    : amountVal <= 0
    ? tr("The investment amount must be greater than 0.")
    : "";
  const rateError = !touched.rate
    ? ""
    : isNaN(rateVal)
    ? tr("Please enter a valid interest rate (e.g. 3.75).")
    : rateVal < 0
    ? tr("The interest rate cannot be negative.")
    : "";

  const valid =
    !isNaN(amountVal) && amountVal > 0 && !isNaN(rateVal) && rateVal >= 0;

  const { interest, total } = useMemo(() => {
    if (!valid) return { interest: 0, total: 0 };
    const i = amountVal * (rateVal / 100) * (term / 12);
    return { interest: i, total: amountVal + i };
  }, [valid, amountVal, rateVal, term]);

  const reset = () => {
    setAmount("50,000");
    setRate("4.00");
    setTerm(12);
    setTouched({ amount: false, rate: false });
  };

  const interestPct =
    valid && total > 0 ? Math.min((interest / total) * 100, 100) : 0;

  return (
    <section id="calculator" className="bg-[#f6f2e9] py-20 md:py-28 scroll-mt-20">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12 reveal">
          <div className="deco-line w-24 mx-auto mb-6" />
          <h2 className="font-display text-[34px] md:text-[46px] text-[#0b1f3a] leading-tight">
            {tr("Fixed-term deposit calculator")}
          </h2>
          <p className="text-[#0b1f3a]/60 text-[17px] mt-4">
            {tr("Estimate your interest and total payout at maturity.")}
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-6 reveal">
          {/* ===== Inputs ===== */}
          <div className="lg:col-span-2 rounded-3xl bg-white ring-1 ring-[#0b1f3a]/8 p-7 md:p-8 shadow-xl shadow-[#0b1f3a]/5">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-[22px] text-[#0b1f3a]">
                {tr("Your details")}
              </h3>
              <button
                onClick={reset}
                className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[#0b1f3a]/55 hover:text-[#c2a15a] transition-colors"
              >
                <RotateCcw size={14} /> {tr("Reset")}
              </button>
            </div>

            {/* Amount */}
            <Field label={tr("Investment amount (€)")} error={amountError} htmlFor="calc-amount">
              <input
                id="calc-amount"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, amount: true }))}
                placeholder="50,000"
                className={`rh-input ${amountError ? "border-red-400" : ""}`}
              />
            </Field>

            {/* Rate */}
            <Field label={tr("Annual interest rate (%)")} error={rateError} htmlFor="calc-rate">
              <input
                id="calc-rate"
                inputMode="decimal"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, rate: true }))}
                placeholder="4.00"
                className={`rh-input ${rateError ? "border-red-400" : ""}`}
              />
            </Field>

            {/* Term */}
            <div className="mb-1">
              <span className="block text-[13px] font-medium text-[#0b1f3a]/70 mb-2">
                {tr("Investment term")}
              </span>
              <div className="grid grid-cols-4 gap-2">
                {TERMS.map((m) => (
                  <button
                    key={m}
                    onClick={() => setTerm(m)}
                    className={`py-2.5 rounded-lg text-[14px] font-medium border transition-colors ${
                      term === m
                        ? "bg-[#0b1f3a] text-white border-[#0b1f3a]"
                        : "border-[#0b1f3a]/15 text-[#0b1f3a]/65 hover:border-[#c2a15a]"
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
              <p className="text-[12px] text-[#0b1f3a]/45 mt-1.5">{tr("Months")}</p>
            </div>
          </div>

          {/* ===== Results ===== */}
          <div className="lg:col-span-3 rounded-3xl bg-[#0b1f3a] p-7 md:p-9 shadow-2xl shadow-[#0b1f3a]/25 flex flex-col">
            <h3 className="font-display text-[22px] text-white mb-6">
              {tr("Your result")}
            </h3>

            {/* Echo of inputs */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              <Echo label={tr("Investment")} value={valid ? fmtEur(amountVal) : "—"} />
              <Echo
                label={tr("Rate p.a.")}
                value={valid ? `${rateVal.toLocaleString("en-GB")} %` : "—"}
              />
              <Echo label={tr("Term")} value={tr("{n} months", { n: term })} />
            </div>

            {/* Visual split */}
            <div className="mb-6">
              <div className="flex justify-between text-[12px] text-white/55 mb-2">
                <span>{tr("Principal")}</span>
                <span>{tr("Interest")}</span>
              </div>
              <div className="h-3 rounded-full bg-white/10 overflow-hidden flex">
                <div className="h-full bg-[#d8bd82]" style={{ width: `${100 - interestPct}%` }} />
                <div className="h-full bg-[#c2a15a]" style={{ width: `${interestPct}%` }} />
              </div>
            </div>

            {/* Headline results */}
            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div className="rounded-2xl bg-white/[0.05] ring-1 ring-[#c2a15a]/25 p-5">
                <p className="text-[12px] uppercase tracking-widest text-white/50">
                  {tr("Interest earned (gross)")}
                </p>
                <p className="font-display text-[#c2a15a] text-[36px] leading-none mt-2">
                  {valid ? fmtEur(interest) : "—"}
                </p>
              </div>
              <div className="rounded-2xl bg-white/[0.05] ring-1 ring-[#c2a15a]/25 p-5">
                <p className="text-[12px] uppercase tracking-widest text-white/50">
                  {tr("Total payout at maturity (gross)")}
                </p>
                <p className="font-display text-white text-[36px] leading-none mt-2">
                  {valid ? fmtEur(total) : "—"}
                </p>
              </div>
            </div>

            <p className="text-[12px] text-white/40 leading-relaxed mb-6">
              {tr("All figures are")} <strong>{tr("gross amounts")}</strong> {tr("before taxes and any applicable fees. This calculation is an estimate only (interest = amount × rate/100 × months/12). The actual payout depends on the individual bank's contractual terms and does not represent a specific offer.")}
            </p>
          </div>
        </div>
        <CurrencyBuffer />
      </div>
    </section>
  );
}

const Field = ({ label, error, htmlFor, children }) => (
  <div className="mb-5">
    <label htmlFor={htmlFor} className="block text-[13px] font-medium text-[#0b1f3a]/70 mb-2">
      {label}
    </label>
    {children}
    {error && (
      <p className="flex items-center gap-1.5 text-[12.5px] text-red-600 mt-1.5">
        <AlertCircle size={13} /> {error}
      </p>
    )}
  </div>
);

const Echo = ({ label, value }) => (
  <div className="rounded-xl bg-white/[0.04] ring-1 ring-white/10 px-4 py-3">
    <p className="text-[11px] uppercase tracking-widest text-white/45">{label}</p>
    <p className="text-white text-[15px] font-medium mt-1 truncate">{value}</p>
  </div>
);
